import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


// Open Food Facts API
const OPEN_FOOD_FACTS_API = 'https://world.openfoodfacts.org/api/v0/product';

const lookupOpenFoodFacts = async (barcode) => {
  try {
    const response = await fetch(`${OPEN_FOOD_FACTS_API}/${barcode}.json`);
    if (!response?.ok) return null;
    const data = await response?.json();
    if (data?.status !== 1 || !data?.product) return null;
    const p = data?.product;
    const nutriments = p?.nutriments || {};
    return {
      name: p?.product_name_es || p?.product_name || p?.generic_name || 'Producto desconocido',
      brand: p?.brands || '',
      calories: Math.round(nutriments?.['energy-kcal_100g'] || nutriments?.['energy-kcal'] || 0),
      protein: parseFloat((nutriments?.proteins_100g || 0)?.toFixed(1)),
      carbs: parseFloat((nutriments?.carbohydrates_100g || 0)?.toFixed(1)),
      fat: parseFloat((nutriments?.fat_100g || 0)?.toFixed(1)),
      fiber: parseFloat((nutriments?.fiber_100g || 0)?.toFixed(1)),
      servingSize: p?.serving_size || '100g',
      defaultServing: 100,
      category: 'Escaneado',
      id: `barcode-${barcode}`,
      barcode,
    };
  } catch {
    return null;
  }
};

const matchLocalDatabase = (barcode) => {
  // Try to match by barcode in local food database (future extension)
  // For now, return null to fall through to Open Food Facts
  return null;
};

const BarcodeScannerModal = ({ isOpen, onClose, onFoodFound }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const codeReaderRef = useRef(null);
  const scanningRef = useRef(false);

  const [scanState, setScanState] = useState('idle'); // idle | requesting | scanning | found | error | manual
  const [errorMsg, setErrorMsg] = useState('');
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [foundProduct, setFoundProduct] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [scanFlash, setScanFlash] = useState(false);

  const stopCamera = useCallback(() => {
    scanningRef.current = false;
    if (codeReaderRef?.current) {
      try { codeReaderRef?.current?.reset(); } catch {}
      codeReaderRef.current = null;
    }
    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach(t => t?.stop());
      streamRef.current = null;
    }
    if (videoRef?.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const handleBarcodeDetected = useCallback(async (barcode) => {
    if (!scanningRef?.current) return;
    scanningRef.current = false;
    setScannedBarcode(barcode);
    setScanFlash(true);
    setTimeout(() => setScanFlash(false), 400);
    setScanState('found');
    stopCamera();
    setIsLookingUp(true);

    // 1. Check local Peruvian database first
    const localMatch = matchLocalDatabase(barcode);
    if (localMatch) {
      setFoundProduct(localMatch);
      setIsLookingUp(false);
      return;
    }

    // 2. Query Open Food Facts
    const product = await lookupOpenFoodFacts(barcode);
    setFoundProduct(product);
    setIsLookingUp(false);
  }, [stopCamera]);

  const startScanning = useCallback(async (deviceId) => {
    setScanState('requesting');
    setErrorMsg('');
    try {
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;

      // Get available cameras
      const devices = await BrowserMultiFormatReader?.listVideoInputDevices();
      setCameras(devices);

      const targetDeviceId = deviceId || (devices?.length > 0 ? devices?.[devices?.length - 1]?.deviceId : undefined);
      if (!selectedCamera && targetDeviceId) setSelectedCamera(targetDeviceId);

      setScanState('scanning');
      scanningRef.current = true;

      await codeReader?.decodeFromVideoDevice(
        targetDeviceId,
        videoRef?.current,
        (result, err) => {
          if (result && scanningRef?.current) {
            handleBarcodeDetected(result?.getText());
          }
        }
      );
    } catch (err) {
      const msg = err?.name === 'NotAllowedError' ?'Permiso de cámara denegado. Activa el acceso a la cámara en tu navegador.'
        : err?.name === 'NotFoundError' ?'No se encontró cámara en este dispositivo.' :'No se pudo acceder a la cámara. Intenta el ingreso manual.';
      setErrorMsg(msg);
      setScanState('error');
    }
  }, [handleBarcodeDetected, selectedCamera]);

  useEffect(() => {
    if (isOpen) {
      setScanState('idle');
      setScannedBarcode('');
      setFoundProduct(null);
      setManualBarcode('');
      setIsLookingUp(false);
      startScanning();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [isOpen]);

  const handleSwitchCamera = (deviceId) => {
    setSelectedCamera(deviceId);
    stopCamera();
    setTimeout(() => startScanning(deviceId), 300);
  };

  const handleManualLookup = async () => {
    if (!manualBarcode?.trim()) return;
    await handleBarcodeDetected(manualBarcode?.trim());
  };

  const handleRescan = () => {
    setScannedBarcode('');
    setFoundProduct(null);
    setManualBarcode('');
    setIsLookingUp(false);
    startScanning(selectedCamera || undefined);
  };

  const handleUseProduct = () => {
    if (foundProduct) {
      onFoodFound(foundProduct);
      onClose();
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative w-full max-w-md bg-card rounded-xl shadow-wellness-lg overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border/40">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="ScanLine" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Escanear Código de Barras</h2>
              <p className="text-xs text-muted-foreground">Apunta la cámara al código del producto</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose} className="text-muted-foreground">
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-4">
          {/* Camera View */}
          {(scanState === 'requesting' || scanState === 'scanning') && (
            <div className="relative mb-4">
              <div className={`relative rounded-xl overflow-hidden bg-black aspect-[4/3] ${
                scanFlash ? 'ring-4 ring-secondary' : ''
              }`}>
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  autoPlay
                  playsInline
                  muted
                />
                {/* Scanning overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-56 h-36">
                    {/* Corner brackets */}
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-secondary rounded-tl" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-secondary rounded-tr" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-secondary rounded-bl" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary rounded-br" />
                    {/* Scan line animation */}
                    {scanState === 'scanning' && (
                      <div className="absolute left-1 right-1 h-0.5 bg-secondary/80 animate-scan-line" />
                    )}
                  </div>
                </div>
                {scanState === 'requesting' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <div className="text-center text-white">
                      <Icon name="Loader2" size={32} className="animate-spin mx-auto mb-2" />
                      <p className="text-sm">Iniciando cámara...</p>
                    </div>
                  </div>
                )}
              </div>
              {/* Camera selector */}
              {cameras?.length > 1 && (
                <div className="mt-2 flex gap-2 overflow-x-auto">
                  {cameras?.map((cam, i) => (
                    <button
                      key={cam?.deviceId}
                      onClick={() => handleSwitchCamera(cam?.deviceId)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                        selectedCamera === cam?.deviceId
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted/40 text-muted-foreground hover:bg-muted/60'
                      }`}
                    >
                      {cam?.label || `Cámara ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-center text-xs text-muted-foreground mt-2">
                Alinea el código de barras dentro del recuadro
              </p>
            </div>
          )}

          {/* Error State */}
          {scanState === 'error' && (
            <div className="mb-4 p-4 bg-destructive/10 rounded-xl border border-destructive/20 text-center">
              <Icon name="CameraOff" size={32} className="text-destructive mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground mb-1">Error de Cámara</p>
              <p className="text-xs text-muted-foreground">{errorMsg}</p>
            </div>
          )}

          {/* Found / Looking up */}
          {scanState === 'found' && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3 p-3 bg-secondary/10 rounded-lg border border-secondary/20">
                <Icon name="CheckCircle" size={18} className="text-secondary shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Código detectado</p>
                  <p className="text-sm font-mono font-medium text-foreground">{scannedBarcode}</p>
                </div>
              </div>

              {isLookingUp && (
                <div className="flex items-center justify-center gap-2 py-6">
                  <Icon name="Loader2" size={20} className="animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Buscando información nutricional...</p>
                </div>
              )}

              {!isLookingUp && foundProduct && (
                <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{foundProduct?.name}</h3>
                      {foundProduct?.brand && (
                        <p className="text-xs text-muted-foreground">{foundProduct?.brand}</p>
                      )}
                    </div>
                    <span className="ml-2 text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded shrink-0">
                      {foundProduct?.servingSize}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center mb-4">
                    {[
                      { label: 'Calorías', value: foundProduct?.calories, unit: 'kcal' },
                      { label: 'Proteína', value: foundProduct?.protein, unit: 'g' },
                      { label: 'Carbos', value: foundProduct?.carbs, unit: 'g' },
                      { label: 'Grasa', value: foundProduct?.fat, unit: 'g' },
                    ]?.map(({ label, value, unit }) => (
                      <div key={label} className="bg-background/60 rounded-lg p-2">
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className="font-semibold text-foreground text-sm">{value}</p>
                        <p className="text-xs text-muted-foreground">{unit}</p>
                      </div>
                    ))}
                  </div>
                  <Button variant="default" fullWidth onClick={handleUseProduct}>
                    <Icon name="Plus" size={16} className="mr-2" />
                    Usar este producto
                  </Button>
                </div>
              )}

              {!isLookingUp && !foundProduct && (
                <div className="bg-warning/10 rounded-xl p-4 border border-warning/20 text-center mb-3">
                  <Icon name="PackageSearch" size={28} className="text-warning mx-auto mb-2" />
                  <p className="text-sm font-medium text-foreground mb-1">Producto no encontrado</p>
                  <p className="text-xs text-muted-foreground">No se encontró información para este código. Ingresa los datos manualmente.</p>
                </div>
              )}
            </div>
          )}

          {/* Manual barcode entry (always visible as fallback) */}
          <div className="border-t border-border/30 pt-4">
            <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
              <Icon name="Keyboard" size={13} />
              Ingreso manual de código
            </p>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Ej: 7750000000000"
                value={manualBarcode}
                onChange={(e) => setManualBarcode(e?.target?.value)}
                onKeyDown={(e) => e?.key === 'Enter' && handleManualLookup()}
                className="flex-1 text-sm"
              />
              <Button
                variant="outline"
                onClick={handleManualLookup}
                disabled={!manualBarcode?.trim()}
                className="shrink-0"
              >
                <Icon name="Search" size={16} />
              </Button>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 mt-4">
            {(scanState === 'found' || scanState === 'error') && (
              <Button variant="outline" onClick={handleRescan} className="flex-1">
                <Icon name="RefreshCw" size={15} className="mr-1.5" />
                Volver a escanear
              </Button>
            )}
            <Button variant="ghost" onClick={handleClose} className={scanState === 'scanning' ? 'flex-1' : ''}>
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default BarcodeScannerModal;
